import React, { lazy, Suspense } from 'react';
import { AccessLevel } from '@dev/ee-api/ee-api-models';
import { useSelector } from 'react-redux';
import { AvailableFeaturesUpselling } from '@dev/ee-modules';

import DeviceDetails from '@/components/Activity/DeviceDetails';
import Geography from '@/components/Activity/Geography';
import ViewEmail from '@/components/Activity/ViewEmail';
import Automations from '@/components/Automations';
import AutomationReports from '@/components/Automations/AutomationReport';
import ViewContacts from '@/components/Automations/AutomationReport/ViewContacts';
import { CampaignCreator } from '@/components/CampaignCreator';
import { Campaigns } from '@/components/Campaigns';
import CampaignReports from '@/components/Campaigns/CampaignReport';
import CampaignReportsAbSplitLinks from '@/components/Campaigns/CampaignReport/AbSplit/Links';
import CampaignReportInProgress from '@/components/Campaigns/CampaignReport/InProgress';
import CampaignLinkContacts from '@/components/Campaigns/CampaignReport/Links/LinkContacts';
import Contacts from '@/components/Contacts';
import ContactsAdd from '@/components/Contacts/Add';
import CreateSegment from '@/components/Contacts/CreateSegment';
import { CustomFields } from '@/components/Contacts/CustomFields';
import ListCreator from '@/components/Contacts/Lists/Create';
import SingleContactRouter from '@/components/Contacts/SingleContact/SingleContactRouter';
import CampaignsByTarget from '@/components/Contacts/TargetCampaigns';
import ContactsUpload from '@/components/Contacts/Upload';
import Exports from '@/components/Exports';
import FormList from '@/components/FormList';
import Home from '@/components/Home';
import LandingPageList from '@/components/LandingPageList';
import AddAccessTokens from '@/components/Settings/accessTokens/AddAccessToken';
import ManageAccessTokens from '@/components/Settings/accessTokens/ManageAccessTokens';
import Advanced from '@/components/Settings/Advanced';
import Domains from '@/components/Settings/Domains';
import DomainsAdvanced from '@/components/Settings/DomainsAdvanced';
import ManageInboundRoutes from '@/components/Settings/InboundRoutes';
import SingleInbound from '@/components/Settings/InboundRoutes/SingleInbound';
import Settings from '@/components/Settings/Overview';
import ManagePrivateIPs from '@/components/Settings/PrivateIP';
import ManagePools from '@/components/Settings/PrivateIP/ManagePools';
import SingleIpView from '@/components/Settings/PrivateIP/SingleIpView';
import SystemOptions from '@/components/Settings/SystemOptions';
import TrackingAndPruning from '@/components/Settings/TrackingAndPruning';
import Unsubscribe from '@/components/Settings/Unsubscribe';
import ManageWebhooks from '@/components/Settings/Webhooks';
import SingleWebhook from '@/components/Settings/Webhooks/SingleWebhook';
import TemplateList from '@/components/TemplateList';
import Preview from '@/components/TemplateList/Preview';
import { TemplateEditor } from '@/components/TemplateEditor';
import Loader from '@/services/PageLoader';
import { ListOfContacts } from '@/components/Contacts/Group';
import { ApplicationState } from '@/store';
import { EvsSingleFile, EvsSingleLead, VerificationsTabs } from '@/dashboards/Evs';
import { OnboardingSurvey } from '@/components/Surveys/OnboardingSurvey';
import { FormCreator } from '@/components/FormCreator';
import { LandingPageCreator } from '@/components/LandingPageCreator';
import { Checkouts } from '@/components/Checkouts';
import { CheckoutCreator } from '@/components/CheckoutCreator';
import { PaidNewsletters } from '@/components/PaidNewsletters';
import {
    ActivityPaths,
    APIPaths,
    AutomationPaths,
    CampaignsPaths,
    CheckoutsPaths,
    ContactsPaths,
    EVSPaths,
    ExportsPaths,
    FormsPaths,
    HomePaths,
    LandingPagePaths,
    PaidNewslettersPaths,
    SettingsPaths,
    SurveysPaths,
    TemplatesPaths,
} from './Paths';
import { useDashboardElementVisibility } from '../hooks/useDashboardElementVisibility';
import { useCurrentPlanStatus } from '../hooks/useCurrentPlanStatus';
import { PaidNewsletterCreator } from '@/components/PaidNewsletterCreator';

//import { lazyWithRetry as lazy } from '@dev/ee-tools';

export interface RouteInterface {
    path: string | string[];
    component: React.ReactNode;
    accessLevel?: AccessLevel[];
    disableBanners?: boolean;
    upsellingFeature?: AvailableFeaturesUpselling;
}
const LazyEditor = lazy(() => import('@/components/Editor'));
const LazyAutomationsEditor = lazy(() => import('@/components/Automations/Editor/MainEditor'));
const LazyManageAccessTokens = lazy(() => import('@/components/Settings/accessTokens/ManageAccessTokens'));
const LazyAddAccessTokens = lazy(() => import('@/components/Settings/accessTokens/AddAccessToken'));

/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
const Editor = (props: any): JSX.Element => (
    <Suspense fallback={<Loader />}>
        <LazyEditor {...props} />
    </Suspense>
);

/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
const AutomationsEditor = (props: any): JSX.Element => {
    const { isStandalone, bootedAsChild, routerBasePath } = useSelector((state: ApplicationState) => state.config);
    const {
        upselling: { isAutomationUpsellingVisible },
        automations: { isAutomationTriggerContactAddedByApiVisible },
    } = useDashboardElementVisibility();
    const isStandaloneDash = isStandalone ? isStandalone : false;
    const { productCode } = useCurrentPlanStatus();

    return (
        <Suspense fallback={<Loader />}>
            <LazyAutomationsEditor
                {...props}
                isStandalone={isStandaloneDash}
                bootedAsChild={bootedAsChild}
                routerBasePath={routerBasePath}
                isAutomationUpsellingVisible={isAutomationUpsellingVisible}
                isAutomationTriggerContactAddedByApiVisible={isAutomationTriggerContactAddedByApiVisible}
                productCode={productCode}
            />
        </Suspense>
    );
};

/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
const UnifiedDashManageAccessTokens = (props: any): JSX.Element => (
    <Suspense fallback={<Loader />}>
        <LazyManageAccessTokens {...props} marketingDashboard unifiedProduct />
    </Suspense>
);

/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
const UnifiedDashAddccessTokens = (props: any): JSX.Element => (
    <Suspense fallback={<Loader />}>
        <LazyAddAccessTokens {...props} marketingDashboard unifiedProduct />
    </Suspense>
);

export const ActivityRoutes: RouteInterface[] = [
    { path: ActivityPaths.DeviceDetails, component: DeviceDetails, accessLevel: [AccessLevel.ViewReports] },
    { path: ActivityPaths.Geography, component: Geography, accessLevel: [AccessLevel.ViewReports] },
];

export const TemplatesRoutes: RouteInterface[] = [
    { path: TemplatesPaths.TemplateList, component: TemplateList, accessLevel: [AccessLevel.ViewTemplates] },
    {
        path: TemplatesPaths.TemplateEditorName,
        component: Editor,
        accessLevel: [AccessLevel.ModifyTemplates, AccessLevel.ViewHTMLEditor],
        disableBanners: true,
    },
    {
        path: TemplatesPaths.TemplateEditorLinkName,
        component: TemplateEditor,
        accessLevel: [AccessLevel.ModifyTemplates],
        disableBanners: true,
    },
    { path: TemplatesPaths.PreviewLink, component: Preview, accessLevel: [AccessLevel.ViewTemplates] },
    { path: TemplatesPaths.PreviewLinkPublic, component: Preview, accessLevel: [AccessLevel.ViewTemplates] },
];

export const LandingsRoutes: RouteInterface[] = [
    { path: LandingPagePaths.LandingPageList, component: LandingPageList, accessLevel: [AccessLevel.ViewLandingPages] },
    {
        path: LandingPagePaths.LandingPageCreator,
        component: LandingPageCreator,
        accessLevel: [AccessLevel.ModifyLandingPages],
    },
];

export const CheckoutsRoutes: RouteInterface[] = [
    {
        path: CheckoutsPaths.CheckoutCreator,
        component: CheckoutCreator,
        accessLevel: [AccessLevel.ViewLandingPagesCheckout],
    },
    { path: CheckoutsPaths.Checkouts, component: Checkouts, accessLevel: [AccessLevel.ViewLandingPagesCheckout] },
];

export const PaidNewslettersRoutes: RouteInterface[] = [
    {
        path: PaidNewslettersPaths.PaidNewsletterCreator,
        component: PaidNewsletterCreator,
        accessLevel: [AccessLevel.ViewPaidNewsletters],
    },
    { path: PaidNewslettersPaths.PaidNewsletters, component: PaidNewsletters, accessLevel: [AccessLevel.ViewPaidNewsletters] },
];

export const RawEditorRoutes: RouteInterface[] = [
    {
        path: LandingPagePaths.TemplateEditorName,
        component: Editor,
        accessLevel: [AccessLevel.ModifyLandingPages, AccessLevel.ViewHTMLEditor],
        disableBanners: true,
    },
];

export const HomeRoutes: RouteInterface[] = [
    { path: HomePaths.Home, component: Home, accessLevel: [AccessLevel.ViewAccount] },
    { path: HomePaths.Dashboard, component: Home, accessLevel: [AccessLevel.ViewAccount] },
];

export const FormsRoutes: RouteInterface[] = [
    { path: FormsPaths.FormList, component: FormList, accessLevel: [AccessLevel.ViewForms] },
    { path: FormsPaths.FormCreator, component: FormCreator, accessLevel: [AccessLevel.ModifyForms] },
    { path: FormsPaths.FormPopupCreator, component: FormCreator, accessLevel: [AccessLevel.ModifyForms] },
];

export const SettingsRoutes: RouteInterface[] = [
    {
        path: SettingsPaths.Settings,
        component: Settings,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: SettingsPaths.SystemOptions,
        component: SystemOptions,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: SettingsPaths.TrackingAndPruning,
        component: TrackingAndPruning,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: SettingsPaths.Advanced,
        component: Advanced,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: SettingsPaths.Unsubscribe,
        component: Unsubscribe,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: [
            SettingsPaths.Domains,
            SettingsPaths.DomainsDomainVerification,
            SettingsPaths.DomainsEmailVerification,
            SettingsPaths.SingleDomainVerification,
            SettingsPaths.EmailAddressVerification,
            SettingsPaths.SingleDomainBounceDomain,
        ],
        component: Domains,
        accessLevel: [AccessLevel.ViewSettings],
    },
    { path: SettingsPaths.DomainsAdvanced, component: DomainsAdvanced, accessLevel: [AccessLevel.ViewSettings] },
    {
        path: SettingsPaths.CreateSMTP,
        component: AddAccessTokens,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: SettingsPaths.ManageSMTP,
        component: ManageAccessTokens,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: SettingsPaths.EditSMTP,
        component: AddAccessTokens,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: SettingsPaths.CreateAPI,
        component: AddAccessTokens,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: SettingsPaths.EditAPI,
        component: AddAccessTokens,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: SettingsPaths.ManageAPI,
        component: ManageAccessTokens,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: SettingsPaths.ManagePrivateIPs,
        component: ManagePrivateIPs,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: SettingsPaths.SingleIP,
        component: SingleIpView,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: SettingsPaths.ManagePools,
        component: ManagePools,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: SettingsPaths.ManageInbounds,
        component: ManageInboundRoutes,
        accessLevel: [AccessLevel.ViewSettings, AccessLevel.ViewWebNotifications],
        upsellingFeature: AvailableFeaturesUpselling.InboundEmails,
    },
    {
        path: SettingsPaths.CreateInbound,
        component: SingleInbound,
        accessLevel: [AccessLevel.ViewSettings, AccessLevel.ViewWebNotifications],
        upsellingFeature: AvailableFeaturesUpselling.InboundEmails,
    },
    {
        path: SettingsPaths.EditInbound,
        component: SingleInbound,
        accessLevel: [AccessLevel.ViewSettings, AccessLevel.ViewWebNotifications],
        upsellingFeature: AvailableFeaturesUpselling.InboundEmails,
    },
    {
        path: SettingsPaths.ManageWebhooks,
        component: ManageWebhooks,
        accessLevel: [AccessLevel.ViewSettings, AccessLevel.ViewWebNotifications],
        upsellingFeature: AvailableFeaturesUpselling.Webhooks,
    },
    {
        path: SettingsPaths.CreateWebhook,
        component: SingleWebhook,
        accessLevel: [AccessLevel.ViewSettings, AccessLevel.ViewWebNotifications],
        upsellingFeature: AvailableFeaturesUpselling.Webhooks,
    },
    {
        path: SettingsPaths.EditWebhook,
        component: SingleWebhook,
        accessLevel: [AccessLevel.ViewSettings, AccessLevel.ViewWebNotifications],
        upsellingFeature: AvailableFeaturesUpselling.Webhooks,
    },
];

/*eslint-enable */

export const ContactsRoutes: RouteInterface[] = [
    {
        path: ContactsPaths.ContactsOverview,
        component: Contacts,
        accessLevel: [AccessLevel.ViewContacts],
    },
    {
        path: ContactsPaths.Contacts,
        component: Contacts,
        accessLevel: [AccessLevel.ViewContacts],
    },
    {
        path: [ContactsPaths.ContactsUpload, ContactsPaths.ContactsUploadImportMailchimp],
        component: ContactsUpload,
        accessLevel: [AccessLevel.ModifyContacts],
    },
    {
        path: ContactsPaths.CustomFields,
        component: CustomFields,
        accessLevel: [AccessLevel.ViewContacts, AccessLevel.ViewCustomFields],
        upsellingFeature: AvailableFeaturesUpselling.CustomFields,
    },
    {
        path: ContactsPaths.ContactsHistory,
        component: Contacts,
        accessLevel: [AccessLevel.ViewContacts],
    },
    {
        path: ContactsPaths.ContactsAdd,
        component: ContactsAdd,
        accessLevel: [AccessLevel.ModifyContacts],
    },
    {
        path: ContactsPaths.Unsubscribed,
        component: Contacts,
        accessLevel: [AccessLevel.ViewContacts],
    },
    {
        path: ContactsPaths.DeviceDetails,
        component: Contacts,
        accessLevel: [AccessLevel.ViewContacts],
    },
    {
        path: ContactsPaths.Geography,
        component: Contacts,
        accessLevel: [AccessLevel.ViewContacts],
    },
    {
        path: ContactsPaths.Segments,
        component: Contacts,
        accessLevel: [AccessLevel.ViewContacts],
    },
    {
        path: ContactsPaths.SegmentEditor,
        component: CreateSegment,
        accessLevel: [AccessLevel.ViewContacts],
    },
    {
        path: ContactsPaths.SegmentsByTarget,
        component: CampaignsByTarget,
        accessLevel: [AccessLevel.ViewContacts],
    },
    {
        path: ContactsPaths.SegmentAdd,
        component: ContactsAdd,
        accessLevel: [AccessLevel.ModifyContacts],
    },
    {
        path: ContactsPaths.SegmentUpload,
        component: ContactsUpload,
        accessLevel: [AccessLevel.ModifyContacts],
    },
    {
        path: ContactsPaths.SegmentSingleContact,
        component: SingleContactRouter,
        accessLevel: [AccessLevel.ViewContacts],
    },
    {
        path: ContactsPaths.SingleSegment,
        component: ListOfContacts,
        accessLevel: [AccessLevel.ViewContacts],
    },
    {
        path: ContactsPaths.SegmentCreator,
        component: CreateSegment,
        accessLevel: [AccessLevel.ViewContacts],
    },
    {
        path: ContactsPaths.Lists,
        component: Contacts,
        accessLevel: [AccessLevel.ViewContacts],
    },
    {
        path: ContactsPaths.ListCreator,
        component: ListCreator,
        accessLevel: [AccessLevel.ViewContacts],
    },
    {
        path: ContactsPaths.ListEditor,
        component: ListCreator,
        accessLevel: [AccessLevel.ViewContacts],
    },
    {
        path: ContactsPaths.SingleList,
        component: ListOfContacts,
        accessLevel: [AccessLevel.ViewContacts],
    },

    {
        path: ContactsPaths.ListAdd,
        component: ContactsAdd,
        accessLevel: [AccessLevel.ModifyContacts],
    },
    {
        path: ContactsPaths.ListUpload,
        component: ContactsUpload,
        accessLevel: [AccessLevel.ModifyContacts],
    },
    {
        path: ContactsPaths.ListTargets,
        component: CampaignsByTarget,
        accessLevel: [AccessLevel.ViewContacts],
    },
    {
        path: ContactsPaths.ListSingleContact,
        component: SingleContactRouter,
        accessLevel: [AccessLevel.ViewContacts],
    },
    {
        path: ContactsPaths.SingleContact,
        component: SingleContactRouter,
        accessLevel: [AccessLevel.ViewContacts],
    },
    {
        path: ContactsPaths.EmailView,
        component: ViewEmail,
        accessLevel: [AccessLevel.ViewContacts],
    },
];

export const AutomationsRoutes: RouteInterface[] = [
    {
        path: AutomationPaths.AutomationNew,
        component: AutomationsEditor,
        accessLevel: [AccessLevel.ModifyAutomations],
        disableBanners: true,
        upsellingFeature: AvailableFeaturesUpselling.Automations,
    },
    {
        path: AutomationPaths.Automations,
        component: Automations,
        accessLevel: [AccessLevel.ViewAutomations],
        upsellingFeature: AvailableFeaturesUpselling.Automations,
    },
    {
        path: AutomationPaths.AutomationEditor,
        component: AutomationsEditor,
        accessLevel: [AccessLevel.ModifyAutomations],
        disableBanners: true,
        upsellingFeature: AvailableFeaturesUpselling.Automations,
    },
    {
        path: AutomationPaths.AutomationPreview,
        component: AutomationsEditor,
        accessLevel: [AccessLevel.ViewAutomations],
        disableBanners: true,
        upsellingFeature: AvailableFeaturesUpselling.Automations,
    },
    {
        path: AutomationPaths.AutomationReport,
        component: AutomationReports,
        accessLevel: [AccessLevel.ViewAutomations],
        upsellingFeature: AvailableFeaturesUpselling.Automations,
    },
    {
        path: AutomationPaths.ViewContacts,
        component: ViewContacts,
        accessLevel: [AccessLevel.ViewAutomations],
        upsellingFeature: AvailableFeaturesUpselling.Automations,
    },
];

export const CampaignsRoutes: RouteInterface[] = [
    {
        path: CampaignsPaths.CampaignReportInProgress,
        component: CampaignReportInProgress,
        accessLevel: [AccessLevel.ViewCampaigns],
    },
    {
        path: CampaignsPaths.CampaignReportAbSplitLinks,
        component: CampaignReportsAbSplitLinks,
        accessLevel: [AccessLevel.ViewCampaigns],
    },
    {
        path: CampaignsPaths.CampaignReportInProgressActivity,
        component: CampaignReportInProgress,
        accessLevel: [AccessLevel.ViewCampaigns],
    },
    {
        path: CampaignsPaths.Campaigns,
        component: Campaigns,
        accessLevel: [AccessLevel.ViewCampaigns],
    },
    {
        path: CampaignsPaths.CampaignReport,
        component: CampaignReports,
        accessLevel: [AccessLevel.ViewCampaigns],
    },

    {
        path: CampaignsPaths.CampaignLinkContacts,
        component: CampaignLinkContacts,
        accessLevel: [AccessLevel.ViewCampaigns],
    },
    {
        path: [
            CampaignsPaths.CampaignCreator,
            CampaignsPaths.CampaignCreatorName,
            CampaignsPaths.CampaignCreatorTarget,
            CampaignsPaths.CampaignCreatorTemplate,
        ],
        component: CampaignCreator,
        accessLevel: [AccessLevel.ModifyCampaigns],
    },
    {
        path: [CampaignsPaths.CampaignCreatorDetails],
        component: CampaignCreator,
        accessLevel: [AccessLevel.ViewCampaigns],
    },
];

export const ExportRoutes: RouteInterface[] = [
    {
        path: ExportsPaths.Exports,
        component: Exports,
        accessLevel: [AccessLevel.ViewAccount],
    },
];

export const EmailAPIRoutes: RouteInterface[] = [
    {
        path: APIPaths.unifiedDashManageSMTP,
        component: UnifiedDashManageAccessTokens,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: APIPaths.unifiedDashManageAPI,
        component: UnifiedDashManageAccessTokens,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: APIPaths.unifiedDashCreateSMTP,
        component: UnifiedDashAddccessTokens,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: APIPaths.unifiedDashCreateAPI,
        component: UnifiedDashAddccessTokens,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: APIPaths.unifiedDashEditSMTP,
        component: UnifiedDashAddccessTokens,
        accessLevel: [AccessLevel.ViewSettings],
    },
    {
        path: APIPaths.unifiedDashEditAPI,
        component: UnifiedDashAddccessTokens,
        accessLevel: [AccessLevel.ViewSettings],
    },
];

export const EVSRoutes: RouteInterface[] = [
    {
        path: EVSPaths.SingleFile,
        component: EvsSingleFile,
        accessLevel: [AccessLevel.ViewVerifyEmails],
    },
    {
        path: EVSPaths.SingleLead,
        component: EvsSingleLead,
        accessLevel: [AccessLevel.ViewVerifyEmails],
    },
    {
        path: EVSPaths.VerificationsTabs,
        component: VerificationsTabs,
        accessLevel: [AccessLevel.ViewVerifyEmails],
    },
    {
        path: EVSPaths.BulkVerification,
        component: VerificationsTabs,
        accessLevel: [AccessLevel.ViewVerifyEmails],
    },
];

export const OnboardingSurveyRoutes: RouteInterface[] = [
    {
        path: SurveysPaths.Onboarding,
        component: OnboardingSurvey,
        accessLevel: [AccessLevel.ViewAccount],
    },
];
